import { SmileTwoTone } from '@ant-design/icons';
import { getOperationName } from '@apollo/client/utilities';
import { Button, List, ListProps, Result, Space, Typography } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { formatDateTime } from '../../utils/format';
import { NotificationsDocument } from '../NotificationBadge/Notifications.generated';
import NotificationSeverity from '../NotificationSeverity';
import NotificationTag from '../NotificationTag';
import { useUser } from '../UserProvider';

import {
  NotificationsQuery,
  useNotificationsQuery,
} from './Notifications.generated';
import styles from './styles.module.less';
import { useUpdateNotificationsMutation } from './UpdateNotifications.generated';

type Notification = NonNullable<
  NotificationsQuery['notifications']['edges']
>[number]['node'];

const NotificationList: FC<ListProps<Notification>> = () => {
  const location = useLocation();
  const history = useHistory();
  const { id: userId } = useUser();

  const { data, loading, refetch } = useNotificationsQuery({
    variables: {
      filter: {
        read: false,
        userId,
      },
      limit: 100,
      offset: 0,
    },
  });

  const [updateNotifications, { loading: updating }] =
    useUpdateNotificationsMutation({
      onCompleted: () => {
        void refetch();
      },
      refetchQueries: [getOperationName(NotificationsDocument) as string],
    });

  const notifications = useMemo(
    () => (data?.notifications.edges || []).map(({ node }) => node),
    [data?.notifications.edges],
  );

  const onMarkAsRead = useCallback(() => {
    void updateNotifications({
      variables: {
        filter: {
          read: false,
          userId,
        },
        read: true,
      },
    });
  }, [updateNotifications, userId]);

  return (
    <>
      <List
        className={styles.list}
        dataSource={notifications}
        loading={loading}
        locale={{
          emptyText: (
            <Result
              className={styles.result}
              icon={<SmileTwoTone twoToneColor="#faad14" />}
              subTitle="No unread notifications"
              title="All Good"
            />
          ),
        }}
        renderItem={(notification) => (
          <List.Item
            className={styles.item}
            onClick={() =>
              history.push({
                ...location,
                pathname: `${location.pathname}/${notification.id}`,
              })
            }
          >
            <List.Item.Meta
              description={
                <>
                  <Space direction="vertical">
                    <span>
                      <Typography.Paragraph
                        ellipsis={{ rows: 2 }}
                        style={{ marginBottom: 2 }}
                      >
                        {notification.message}
                      </Typography.Paragraph>
                      <Typography.Text type="secondary">
                        {formatDateTime(notification.time as string)}
                      </Typography.Text>
                    </span>
                    <Space>
                      <NotificationSeverity severity={notification.severity} />
                      <NotificationTag tag={notification.tag} />
                    </Space>
                  </Space>
                </>
              }
              title={
                <Typography.Text strong>{notification.title}</Typography.Text>
              }
            />
          </List.Item>
        )}
      />
      {Boolean(!loading && notifications.length) && (
        <div className={styles.footer}>
          <Button
            block
            className={styles.button}
            loading={updating}
            onClick={onMarkAsRead}
            type="link"
          >
            Mark All As Read
          </Button>
        </div>
      )}
    </>
  );
};

NotificationList.displayName = 'NotificationList';

export default NotificationList;
